'use strict';

import { observable, action, configure, observe, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import planItemStore from "./PlanItemStore";
import events from '@uw-it-sis/lib-react/lib/Events';
import PlanEvents from './PlanEvents';
import {isEmpty,v} from "@uw-it-sis/lib-react/lib/Utils";
import {isCoursePlaceholder, normalize} from './CourseUtils';
import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import bookmarksStore from './BookmarksStore';
import {getAggregateState} from '@uw-it-sis/lib-react/lib/StateUtils';
import {GeneralPlaceholders} from './GeneralPlaceHolderConstants';
import GenEdUtils from './GenEdUtils';

class AddEditCourseOrPLaceHolderFormStore {

    state = States.initial;

    data = this.defaultData();

    // For controlling the disabled state of the "add to plan" button
    disableAddToPlan = true;

    constructor() {
        makeObservable(this, {
            state: observable,
            data: observable,
            disableAddToPlan: observable,
            mount: action,
            updateFormData: action,
            submitData: action,
            finishPlanItemAdd: action,
            errorPlanItemAdd: action,
            checkDataLoadStates: action
        });

        this.dataLoadStates = {
            bookmarkLoadState: States.initial,
            internalProcessing: States.initial
        };
    }

    defaultData() {
        return {
            // For tracking if the add plan item action was successful
            successful: false,
            // For tracking that the addToPlanSelector has changed for focus
            initialForm: true,
            // For error messaging
            addFailedMessage: "",
            // default form data
            formData: {
                addToPlanSelector: "course",
                courseCode: "",
                credit: "",
                isBackup: false,
                termId: "",
                note: "",
                placeHolderText: "",
                planItemId: "",
                genedCode: ""
            }
        }
    }

    /**
     * initialize ( or reset ) internal state
     */
    mount(termId, formData) {
        this.data = this.defaultData();
        this.data.formData.termId = termId;
        // Existing form data makes this an edit plan item form
        if (formData) {
            this.data.formData = formData;
        }

        this.dataLoadStates = {
            bookmarkLoadState: States.pending,
            internalProcessing: States.pending
        };

        this.checkDataLoadStates();

        this.bookmarksStoreDisposer = observe(bookmarksStore, "state", function(change) {
            // Set the data state for this store to the new value.
            this.dataLoadStates.bookmarkLoadState = change.newValue;
            this.checkDataLoadStates();
        }.bind(this));

        bookmarksStore.loadBookmarks();

        // Setup a listener for when the planItem has been added
        events.listen(PlanEvents.planItemAdded, (eventObject) => {
            if (eventObject.code.toLowerCase() === this.data.formData.courseCode.toLowerCase()) {
                this.finishPlanItemAdd();
            }
        });

        // Setup a listener for when the planItem has been updated
        events.listen(PlanEvents.planItemUpdated, (eventObject) => {
            if (eventObject.code.toLowerCase() === this.data.formData.courseCode.toLowerCase()) {
                this.finishPlanItemAdd();
            }
        });

        // Setup a listener for when there is an error on adding a planItem
        events.listen(PlanEvents.planItemError, (eventObject) => {
            if (eventObject.code.toLowerCase() === this.data.formData.courseCode.toLowerCase() || eventObject.code.toLowerCase() === normalize(this.data.formData.courseCode).toLowerCase()) {
                this.errorPlanItemAdd(eventObject.errMessage);
            }
        });

        this.dataLoadStates.internalProcessing = States.done;

        this.checkDataLoadStates();
    }

    /**
     * Calls disposers and unregisters listeners.
     */
    unmount() {
        this.bookmarksStoreDisposer();

        events.remove(PlanEvents.planItemAdded, (eventObject) => {
            if (eventObject.code === this.data.formData.courseCode) {
                this.finishPlanItemAdd();
            }
        });

        events.remove(PlanEvents.planItemUpdated, (eventObject) => {
            if (eventObject.code === this.data.formData.courseCode) {
                this.finishPlanItemAdd();
            }
        });

        events.remove(PlanEvents.planItemError, (eventObject) => {
            if (eventObject.code === this.data.formData.courseCode) {
                this.errorPlanItemAdd(eventObject.errMessage);
            }
        });
    }

    updateFormData(property, newValue) {
        if ( ! isEmpty(this.data) ) {
            // If the addToPlanSelector selector is changed, clear other form values
            // as it could be pre-populated with unintended values from previous choices
            if (property === "addToPlanSelector") {
                this.data.formData.courseCode = "";
                this.data.formData.genedCode = "";
                this.data.formData.placeHolderText = "";
            }

            this.data.formData[property] = newValue;
        }

        // Track if the form is an initial load for setting initial focus
        this.data.initialForm = false;

        // Enable the "add to plan" button
        if (this.data.formData.courseCode !== "" && this.data.formData.termId !== "" && this.data.formData.addToPlanSelector !== "") {
            if (this.data.formData.addToPlanSelector === "placeholder"
                && ( ( this.data.formData.courseCode === GeneralPlaceholders.OTHER && this.data.formData.placeHolderText === "" )
                     || ( this.data.formData.courseCode === GeneralPlaceholders.GENERALEDUCATION && this.data.formData.genedCode === "" ))) {
                this.disableAddToPlan = true;
            } else {
                this.disableAddToPlan = false;
            }
        }
        else {
            this.disableAddToPlan = true;
        }
    }

    getFormData() {
        return this.data.formData;
    }

    _isValidFormData() {
        let rVal = true;

        if ( ! this.data.formData ) {
            return false;
        }

        if ( ! v("courseCode", this.data.formData) ) {
            rVal = false;
        }

        if ( ! v("termId", this.data.formData) ) {
            rVal = false;
        }
        return rVal;
    }

    getAddPlanSelector() {
        return this.data.formData["addToPlanSelector"];
    }

    isInitialForm() {
        return this.data.initialForm;
    }

    submitData() {
        this.state = States.pending;
        this.data.addFailedMessage = undefined;

        if (! this._isValidFormData()) {
            this.state = States.initial;
            return;
        }

        let formdata = this.getFormData();
        if ( v("note", this.formData) === undefined ) {
           formdata.note = "";
        }

        if ( v("isBackup", this.formData) === undefined ) {
            formdata.isBackup = "";
        }

        if ( v("placeHolderText", this.formData) === undefined ) {
            formdata.placeHolderText = "";
        }

        if ( v("genedCode", this.formData) === undefined ) {
            formdata.genedCode = "";
        }

        if (this.data.formData.addToPlanSelector === "placeholder" && (this.data.formData.courseCode === GeneralPlaceholders.GENERALEDUCATION
            || GenEdUtils.doesGenedCodeExist(this.data.formData.courseCode)) && this.data.formData.genedCode){
            this.data.formData.courseCode = this.data.formData.genedCode;
        }
        // Check if the course code entered is a placeholder course or a general placeholder
        if (isCoursePlaceholder(formdata.courseCode) || formdata.courseCode.includes("|")) {
            if (formdata.planItemId !== "") {
                planItemStore.updatePlaceHolder(formdata.planItemId, formdata.courseCode, formdata.termId, formdata.note, formdata.isBackup, formdata.credit, formdata.placeHolderText);
            } else {
                planItemStore.addPlaceHolder(formdata.courseCode, formdata.termId, formdata.note, formdata.isBackup, formdata.credit, formdata.placeHolderText);
            }
        } else {
            if (formdata.planItemId !== "") {
                planItemStore.update(formdata.planItemId, formdata.courseCode, formdata.termId, formdata.note);
            } else {
                planItemStore.add(formdata.courseCode, formdata.termId, formdata.note, formdata.isBackup, false);
            }
        }
    }

    finishPlanItemAdd() {
        this.data.successful = true;
        this.state = States.done;
    }

    isSuccessful() {
        return this.data.successful;
    }

    errorPlanItemAdd(errorMessage) {
        this.data.addFailedMessage = errorMessage;
        this.state = States.error;
    }

    /**
     * Returns the error message
     * @returns {string|*}
     */
    getAddFailedMessage() {
        return this.data.addFailedMessage;
    }

    /**
     * Check aggregate data load states and update the overall state if necessary.
     */
    checkDataLoadStates() {
        this.state = getAggregateState(this.dataLoadStates);
    }
}

export default new AddEditCourseOrPLaceHolderFormStore();